import React from "react";
import { Checkbox } from "@/dentlab/src/components/ui/checkbox";
import { Label } from "@/dentlab/src/components/ui/label";
import { CustomDatePicker } from "@/dentlab/src/components/custom-date-picker/custom-date-picker.component";
import { CustomSelectShadcn } from "@/dentlab/src/components/custom-select-shadcn.component";
import { BankAccountSelect } from "@/dentlab/src/forms/form-components/bank-account/input-components/bank-account.select";
import { ProdCountryChoiceSelect } from "@/dentlab/src/forms/form-input-components/selects/prod-country-choice.select";
import { FIELD_REGISTRY } from "@/dentlab/src/pages/job-configuration/field-registry";
import { invCoveredByOptions } from "@/dentlab/src/store/Jobs/utils";
import { patientFormTexts } from "@/dentlab/src/forms/form-components/patient/patient-form-texts";
import { JobPatientSelect } from "./job-patient-select";
import { JobPractitionerSelect } from "./job-practitioner-select";
import { TitleSelectShadcn } from "@/dentlab/src/forms/form-input-components/selects/title.select";
import { CustomTextFieldShadcn } from "@/dentlab/src/forms/form-input-components/custom-input-components/custom-text-field-shadcn.component";
import { CustomTextareaShadcn } from "@/dentlab/src/forms/form-input-components/custom-input-components/custom-textarea-shadcn.component";
import { useJobStore } from "@/dentlab/src/store/Jobs";
import { JobPatientCityPostalCodeCountry } from "../job-fields/job-patient-city-postal-code-country.component";

import { JobInfoTpValue } from "../job-fields/job-info-tp-value.component";
import { JobInfoPatientIsPaying } from "../job-fields/job-info-patient-is-paying.component";
import { JobInfoGuarantorSelect } from "../job-fields/job-info-guarantor-select.component";
import { JobInfoTpVariation } from "../job-fields/job-info-tp-variation.component";
import { PatientFieldAutocomplete } from "../job-fields/patient-field-autocomplete.component";
import { PriceListSelect } from "@/dentlab/src/forms/form-components/price-list-select.component";
import { ROUND_TO_OPTIONS } from "@/dentlab/src/types/region.types";
import { DateTimePicker } from "@/dentlab/src/components/ui/datetime-picker";
import { Logger } from "@/lib/logger/Logger";
import {
	InputWrapper,
	INVALID_CONTROL_CLASS,
} from "@/dentlab/src/components/input-wrapper";
import { cn } from "@/dentlab/src/lib/util";

interface FieldRendererProps {
	fieldId: string;
	required: boolean;
	size?: "small" | "medium";
	inputHeight?: number;
}

export const FieldRenderer: React.FC<FieldRendererProps> = ({
	fieldId,
	required,
	size = "small",
	inputHeight,
}) => {
	const {
		job,

		setPatientField,

		formFieldsJob,
		formFieldsPatient,

		formErrors,

		// For fields that need to be debounced, use setJobField,
		setJobField,

		// For fields that can be saved immediately, use updateJobFields
		updateJobFields,
		updateJobItems,
	} = useJobStore((state) => ({
		job: state.job,
		setJobField: state.setJobField,
		setPatientField: state.setPatientField,
		formFieldsPatient: state.formFieldsPatient,
		formErrors: state.formErrors,
		updateJobFields: state.updateJobFields,
		updateJobItems: state.updateJobItems,
		formFieldsJob: state.formFieldsJob,
	}));
	const fieldProblem = useJobStore((state) => state.fieldProblems[fieldId]);

	const fieldDef = FIELD_REGISTRY[fieldId];
	if (!fieldDef) return null;

	// A typed value that fails input validation outranks a problem reported by an action.
	const [table, column] = fieldId.split(".");
	const validationError =
		formErrors[table as keyof typeof formErrors]?.[column] ||
		fieldProblem?.message ||
		null;
	const invalidClass = validationError ? INVALID_CONTROL_CLASS : undefined;

	const densitySize = inputHeight
		? inputHeight <= 32
			? ("sm" as const)
			: inputHeight <= 40
				? ("md" as const)
				: undefined
		: undefined;

	// Field-specific renderers
	switch (fieldId) {
		case "patient_select":
			return (
				<div className="flex flex-col justify-end h-full">
					<JobPatientSelect
						inputHeight={inputHeight}
						validationError={validationError}
					/>
				</div>
			);

		case "practitioner_select":
			return (
				<JobPractitionerSelect
					inputHeight={inputHeight}
					validationError={validationError}
				/>
			);

		case "jobs.title":
			return (
				<CustomTextFieldShadcn
					label="Auftragstitel"
					name="title"
					value={job?.title || ""}
					onChange={(e) => updateJobFields({ title: e.target.value })}
					densitySize={densitySize}
					inputHeight={inputHeight}
					validationError={validationError}
				/>
			);

		case "jobs.incoming_order_date":
			return (
				<InputWrapper
					label={fieldDef.label}
					validationError={validationError}
				>
					<CustomDatePicker
						key={job?.incoming_order_date ?? ""}
						initialValue={job?.incoming_order_date ?? ""}
						onChange={(value) =>
							updateJobFields({
								incoming_order_date: value,
							})
						}
						size="medium"
						inputHeight={inputHeight}
						className={invalidClass}
					/>
				</InputWrapper>
			);

		case "jobs.outgoing_order_date":
			return (
				<InputWrapper
					label={fieldDef.label}
					validationError={validationError}
				>
					<CustomDatePicker
						key={job?.outgoing_order_date ?? ""}
						initialValue={job?.outgoing_order_date || ""}
						onChange={(value) =>
							updateJobFields({ outgoing_order_date: value })
						}
						size="medium"
						inputHeight={inputHeight}
						className={invalidClass}
					/>
				</InputWrapper>
			);

		case "jobs.incoming_order_datetime": {
			const inDate = job?.incoming_order_date;
			const inTime = job?.incoming_order_time;
			let inValue: Date | undefined;
			if (inDate) {
				const [y, m, d] = inDate.split("-").map(Number);
				const [h, min] = inTime
					? inTime.split(":").map(Number)
					: [0, 0];
				inValue = new Date(y, m - 1, d, h, min);
			}
			return (
				<InputWrapper
					label={fieldDef.label}
					validationError={validationError}
				>
					<DateTimePicker
						value={inValue}
						onChange={(date) => {
							if (!date) {
								updateJobFields({
									incoming_order_date: null,
									incoming_order_time: null,
								});
								return;
							}
							const dateStr = `${date.getFullYear()}-${String(
								date.getMonth() + 1
							).padStart(2, "0")}-${String(
								date.getDate()
							).padStart(2, "0")}`;
							const timeStr = `${String(date.getHours()).padStart(
								2,
								"0"
							)}:${String(date.getMinutes()).padStart(
								2,
								"0"
							)}:00`;
							updateJobFields({
								incoming_order_date: dateStr,
								incoming_order_time: timeStr,
							});
						}}
						aria-label="Eingangsdatum und Uhrzeit"
						data-testid="field-incoming-order-datetime"
						inputHeight={inputHeight}
						className={invalidClass}
					/>
				</InputWrapper>
			);
		}

		case "jobs.outgoing_order_datetime": {
			const outDate = job?.outgoing_order_date;
			const outTime = job?.outgoing_order_time;
			let outValue: Date | undefined;
			if (outDate) {
				const [y, m, d] = outDate.split("-").map(Number);
				const [h, min] = outTime
					? outTime.split(":").map(Number)
					: [0, 0];
				outValue = new Date(y, m - 1, d, h, min);
			}
			return (
				<InputWrapper
					label={fieldDef.label}
					validationError={validationError}
				>
					<DateTimePicker
						value={outValue}
						onChange={(date) => {
							if (!date) {
								updateJobFields({
									outgoing_order_date: null,
									outgoing_order_time: null,
								});
								return;
							}
							const dateStr = `${date.getFullYear()}-${String(
								date.getMonth() + 1
							).padStart(2, "0")}-${String(
								date.getDate()
							).padStart(2, "0")}`;
							const timeStr = `${String(date.getHours()).padStart(
								2,
								"0"
							)}:${String(date.getMinutes()).padStart(
								2,
								"0"
							)}:00`;
							updateJobFields({
								outgoing_order_date: dateStr,
								outgoing_order_time: timeStr,
							});
						}}
						aria-label="Ausgangsdatum und Uhrzeit"
						data-testid="field-outgoing-order-datetime"
						inputHeight={inputHeight}
						className={invalidClass}
					/>
				</InputWrapper>
			);
		}

		case "jobs.tp_value":
			return (
				<JobInfoTpValue
					inputHeight={inputHeight}
					validationError={validationError}
				/>
			);

		case "jobs.description":
			return (
				<CustomTextareaShadcn
					label="Beschreibung"
					name="description"
					value={formFieldsJob.description || ""}
					onChange={(e) => setJobField("description", e.target.value)}
					rows={2}
					densitySize={densitySize}
					inputHeight={inputHeight}
					validationError={validationError}
				/>
			);

		case "jobs.tooth_color":
			return (
				<CustomTextFieldShadcn
					label="Zahnfarbe"
					name="tooth_color"
					value={formFieldsJob.tooth_color || ""}
					onChange={(e) => setJobField("tooth_color", e.target.value)}
					densitySize={densitySize}
					inputHeight={inputHeight}
					validationError={validationError}
				/>
			);

		case "jobs.is_ignore_monthly_discount":
			return (
				<InputWrapper
					validationError={validationError}
					className="flex flex-col justify-end h-full"
				>
					<div
						className={cn(
							"flex items-center space-x-2 border rounded-md px-3",
							invalidClass
						)}
						style={{ height: `${inputHeight ?? 48}px` }}
					>
						<Checkbox
							id="is_ignore_monthly_discount"
							checked={job?.is_ignore_monthly_discount || false}
							onCheckedChange={(checked) =>
								updateJobFields({
									is_ignore_monthly_discount:
										checked as boolean,
								})
							}
						/>
						<Label
							htmlFor="is_ignore_monthly_discount"
							className="text-sm font-normal cursor-pointer"
						>
							Monatsrabatt unterdrücken
						</Label>
					</div>
				</InputWrapper>
			);

		case "jobs.is_paid_by_patient":
			return (
				<JobInfoPatientIsPaying
					disabled={!job?.patient_id}
					inputHeight={inputHeight}
					validationError={validationError}
				/>
			);

		case "jobs.bank_account_id":
			return (
				<BankAccountSelect
					onChange={(value) =>
						updateJobFields({
							bank_account_id: value ? parseInt(value) : null,
						})
					}
					value={
						job?.bank_account_id
							? job.bank_account_id.toString()
							: ""
					}
					inputHeight={inputHeight}
					validationError={validationError}
				/>
			);

		case "jobs.tax_rate":
			return (
				<CustomTextFieldShadcn
					label="MwSt."
					name="tax_rate"
					value={formFieldsJob.tax_rate || ""}
					onChange={(e) => {
						setJobField("tax_rate", e.target.value);
					}}
					densitySize={densitySize}
					inputHeight={inputHeight}
					validationError={validationError}
				/>
			);

		case "jobs.guarantor_id":
			return (
				<JobInfoGuarantorSelect
					inputHeight={inputHeight}
					validationError={validationError}
				/>
			);

		case "jobs.tp_variation":
			return (
				<JobInfoTpVariation
					inputHeight={inputHeight}
					validationError={validationError}
				/>
			);

		case "jobs.prod_country_choice_or_other":
			return (
				<div
					className={
						job?.prod_country_choice === "other"
							? "grid grid-cols-2 gap-2"
							: ""
					}
				>
					<ProdCountryChoiceSelect
						value={job?.prod_country_choice || ""}
						onChange={(value) =>
							updateJobFields({
								prod_country_choice: value
									? value.toString()
									: "",
							})
						}
						inputHeight={inputHeight}
						validationError={validationError}
					/>
					{job?.prod_country_choice === "other" && (
						<CustomTextFieldShadcn
							label="Land"
							name="prod_country_other"
							value={formFieldsJob.prod_country_other || ""}
							onChange={(e) =>
								setJobField(
									"prod_country_other",
									e.target.value
								)
							}
							densitySize={densitySize}
							inputHeight={inputHeight}
						/>
					)}
				</div>
			);

		case "jobs.inv_covered_by":
			return (
				<CustomSelectShadcn
					options={invCoveredByOptions}
					inputLabel="Kostenübernahme"
					onChange={(value) =>
						updateJobFields({
							inv_covered_by: value as string,
						})
					}
					value={job?.inv_covered_by || ""}
					densitySize={densitySize}
					inputHeight={inputHeight}
					validationError={validationError}
				/>
			);

		case "jobs.insurance":
			return (
				<CustomTextFieldShadcn
					label="Versicherung"
					name="insurance"
					customHelperText="Name der Krankenkasse oder Versicherung."
					value={formFieldsJob.insurance || ""}
					onChange={(event) =>
						setJobField("insurance", event.target.value)
					}
					densitySize={densitySize}
					inputHeight={inputHeight}
					validationError={validationError}
				/>
			);

		case "jobs.price_list_id":
			return (
				<PriceListSelect
					value={job?.price_list_id || ""}
					onChange={(value) => {
						if (!job?.id || value === null) return;
						updateJobFields({
							price_list_id: value,
						});
						// TODO: First ask in dialog if existing job items should be updated
						//await updateJobItems(job.id, undefined, undefined, value);
					}}
					showEmpty={true}
					inputHeight={inputHeight}
					validationError={validationError}
				/>
			);

		case "jobs.currency_text":
			return (
				<CustomTextFieldShadcn
					label="Währung"
					value={job?.currency_text || ""}
					readOnly={true}
					densitySize={densitySize}
					inputHeight={inputHeight}
				/>
			);

		case "jobs.round_to":
			return (
				<CustomTextFieldShadcn
					label="Rundung"
					value={
						job?.round_to
							? ROUND_TO_OPTIONS.find(
									(opt) => opt.value === job.round_to
								)?.label || "-"
							: "-"
					}
					readOnly={true}
					densitySize={densitySize}
					inputHeight={inputHeight}
				/>
			);

		case "patients.title":
			return (
				<TitleSelectShadcn
					onChange={(value) => setPatientField("title", value)}
					value={formFieldsPatient.title || ""}
					required={required}
					densitySize={densitySize}
					inputHeight={inputHeight}
					validationError={validationError}
				/>
			);

		case "patients.first_name":
			return (
				<PatientFieldAutocomplete
					field="first_name"
					value={formFieldsPatient.first_name || ""}
					required={required}
					densitySize={densitySize}
					inputHeight={inputHeight}
					validationError={validationError}
				/>
			);

		case "patients.last_name":
			return (
				<PatientFieldAutocomplete
					field="last_name"
					value={formFieldsPatient.last_name || ""}
					required={required}
					densitySize={densitySize}
					inputHeight={inputHeight}
					validationError={validationError}
				/>
			);

		case "patients.code":
			return (
				<PatientFieldAutocomplete
					field="code"
					value={formFieldsPatient.code || ""}
					required={required}
					densitySize={densitySize}
					inputHeight={inputHeight}
					validationError={validationError}
					customHelperText="Patientennummer beim Zahnarzt."
				/>
			);

		case "patients.birth_date":
			return (
				<PatientFieldAutocomplete
					field="birth_date"
					value={formFieldsPatient.birth_date || ""}
					densitySize={densitySize}
					inputHeight={inputHeight}
					validationError={validationError}
				/>
			);

		case "patients.street":
			return (
				<CustomTextFieldShadcn
					label="Strasse"
					name="street"
					value={formFieldsPatient.street || ""}
					onChange={(event) =>
						setPatientField("street", event.target.value)
					}
					validationError={validationError}
					densitySize={densitySize}
					inputHeight={inputHeight}
				/>
			);

		case "patients.city_postal_code_country":
			return (
				<JobPatientCityPostalCodeCountry
					disabled={false}
					inputHeight={inputHeight}
					validationError={validationError}
				/>
			);

		case "patients.additional_information":
			return (
				<CustomTextFieldShadcn
					label="Zusatz"
					name="additional_information"
					value={formFieldsPatient.additional_information || ""}
					onChange={(event) =>
						setPatientField(
							"additional_information",
							event.target.value
						)
					}
					validationError={validationError}
					densitySize={densitySize}
					inputHeight={inputHeight}
				/>
			);

		case "patients.gender":
			return (
				<CustomSelectShadcn
					options={patientFormTexts.options.gender}
					inputLabel="Geschlecht"
					onChange={(value) => setPatientField("gender", value)}
					value={formFieldsPatient.gender || ""}
					densitySize={densitySize}
					inputHeight={inputHeight}
					validationError={validationError}
				/>
			);

		case "patients.ahv_number":
			return (
				<CustomTextFieldShadcn
					label="AHV-Nummer"
					name="ahv_number"
					value={formFieldsPatient.ahv_number || ""}
					onChange={(event) =>
						setPatientField("ahv_number", event.target.value)
					}
					validationError={validationError}
					densitySize={densitySize}
					inputHeight={inputHeight}
				/>
			);

		default:
			// This should never happen if all fields are handled above
			Logger.error(`Unhandled field ID in FieldRenderer: ${fieldId}`);
			return null;
	}
};
